import { inject, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { BOOK_SOCKET_ROOM_PREFIX, type BookStatus } from '@lite-workspace/photobook-model';
import { SocketRoomTicketService } from './socket-room-ticket.service';

const ROOM_TYPES = [
  'FILE',
  'FILE_FACES',
  'PERSON',
  'BOOK',
  'PHOTO_BOOK',
  'PHOTO_BOOK_ARCHIVE',
  'ARCHIVE',
  'DESIGN_PACKAGE_ARCHIVE',
  'DESIGN_PACKAGE_INDD',
  'DESIGN_PACKAGE_SPREAD',
  'PRINT_PRODUCT_VERIFICATION',
] as const;
type RoomType = (typeof ROOM_TYPES)[number];

export enum SocketMessage {
  FILE_ALREADY_EXISTS = 'fileAlreadyExists',
  UPLOAD_FILE_PROGRESS = 'uploadFileProgress',
  UPLOAD_FILE_VERSION_DATA = 'uploadFileVersionData',
  FILE_PROCESS_STATUS = 'fileProcessStatus',
  UPLOAD_FILE_ERROR = 'uploadFileError',
  PERSON_CREATED = 'personCreated',
  IS_INDIVIDUAL_STATE = 'isIndividualState',
  PHOTO_BOOK_EXPORT = 'photoBookExport',
  BOOK_STATUS_CHANGED = 'bookStatusChanged',
  BOOK_PRINT_PROGRESS = 'bookPrintProgress',
  BOOK_FILE_VERSION_READY = 'bookFileVersionReady',
  PHOTO_BOOK_COMMENTS_CHANGED = 'photoBookCommentsChanged',
  FILE_FACES_RECOGNIZED = 'fileFacesRecognized',
  IMAGE_ARCHIVE_PROGRESS = 'imageArchiveProgress',
  IMAGE_ARCHIVE_COMPLETED = 'imageArchiveCompleted',
  IMAGE_ARCHIVE_ERROR = 'imageArchiveError',
  DESIGN_PACKAGE_ARCHIVE_EXPORT = 'designPackageArchiveExport',
  PRINT_PRODUCT_VERIFICATION_EXPORT = 'printProductVerificationExport',
  DESIGN_INDD_CONVERT = 'designInddConvert',
  DESIGN_PSD_PARSE = 'designPsdParse',
  // Demo project attach workflow
  DEMO_PROJECT_ATTACH_COMPLETED = 'demoProjectAttachCompleted',
  DEMO_PROJECT_ATTACH_FAILED = 'demoProjectAttachFailed',
  DEMO_PHOTOBOOKS_READY = 'demoPhotobooksReady',
}

export enum SocketMessageIncoming {
  joinUserToRoom = 'joinUserToRoom',
  leaveUserFromRoom = 'leaveUserFromRoom',
  heartbeat = 'heartbeat',
}

export interface PhotoBookExportData {
  bookId: string;
  bookGenState: 'generating' | 'done' | 'error';
  bookHash: string;
  bookGenStartAt: string;
  percent?: number;
  bookZipPath?: string;
  message?: string;
  /** Print product of a variant render (the album rebuilt for another product); absent on the album's own render. */
  variant?: string;
  roomId: string;
}

export interface PhotoBookFullArchiveExportData {
  roomId: string;
  finalUrl: string;
}

/** Standalone photobook status move; `roomId` is the book id the BOOK room is keyed by. */
export interface BookStatusChangedData {
  roomId: string;
  bookId: string;
  status: BookStatus;
  round?: number;
}

/** How far the book's print render got; `percent` rides only the live phase. Twin of the backend's
 * `BookPrintProgressData`. */
export interface BookPrintProgressData {
  roomId: string;
  bookId: string;
  status: 'pending' | 'done' | 'failed';
  percent?: number;
  /** The export row this number belongs to — one book can hold more than one render. */
  generation?: string;
}

/** A cut version of the book's file is readable; `fileId` is the ORIGINAL. Twin of the backend's
 * `BookFileVersionReadyData` — the listener re-reads the versions itself, the event only names the file. */
export interface BookFileVersionReadyData {
  roomId: string;
  bookId: string;
  fileId: string;
}

export interface DesignPackageArchiveExportData {
  roomId: string;
  status: 'ready' | 'error';
  finalUrl?: string;
  hashTask?: string;
  errorMessage?: string;
}

export interface PrintProductVerificationExportData {
  roomId: string;
  productId: string;
  status: 'pending' | 'packing' | 'done' | 'failed';
  zipPath?: string | null;
  message?: string;
}

export interface DesignPsdParseData {
  roomId: string;
  spreadId: string;
  status: 'ready' | 'error';
  errorMessage?: string;
}

interface ChannelSubject<T = any> {
  roomId: string;
  events$: Subject<T>;
}

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private readonly roomTickets = inject(SocketRoomTicketService);
  private socket!: Socket;
  private subjects: ChannelSubject[] = [];
  private wasConnected = false;
  private reconnectedSubject = new Subject<void>();
  private readonly connectedSignal = signal(false);

  /** Эмитит при реконнекте сокета после отключения (не при первом подключении) */
  readonly reconnected$ = this.reconnectedSubject.asObservable();

  /**
   * Живой ли сокет прямо сейчас. Экран, у которого realtime — не единственный источник правды,
   * читает это, чтобы включать свой фолбэк-поллинг только пока новостей ждать неоткуда.
   */
  readonly connected = this.connectedSignal.asReadonly();

  constructor() {
    this.initializeSocket();
  }

  private initializeSocket() {
    this.socket = io('/', {
      path: '/socket.io',
      transports: ['websocket'],
      autoConnect: false,
    });

    this.setupConnectionHandlers();
  }

  private setupConnectionHandlers() {
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.connectedSignal.set(true);
      this.rejoinRooms();

      // Эмитим событие реконнекта (не при первом подключении)
      if (this.wasConnected) {
        this.reconnectedSubject.next();
      }

      this.wasConnected = true;
    });

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
      this.connectedSignal.set(false);
    });

    this.socket.on('error', err => {
      console.error('Socket error:', err);
    });

    this.socket.on(SocketMessage.FILE_ALREADY_EXISTS, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.FILE_ALREADY_EXISTS);
    });

    this.socket.on(SocketMessage.UPLOAD_FILE_PROGRESS, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.UPLOAD_FILE_PROGRESS);
    });

    this.socket.on(SocketMessage.UPLOAD_FILE_VERSION_DATA, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.UPLOAD_FILE_VERSION_DATA);
    });

    this.socket.on(SocketMessage.FILE_PROCESS_STATUS, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.FILE_PROCESS_STATUS);
    });

    this.socket.on(SocketMessage.FILE_FACES_RECOGNIZED, data => {
      const roomId = this.getRoomFullId('FILE_FACES', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.FILE_FACES_RECOGNIZED);
    });

    this.socket.on(SocketMessage.UPLOAD_FILE_ERROR, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.UPLOAD_FILE_ERROR);
    });

    this.socket.on(SocketMessage.IS_INDIVIDUAL_STATE, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.IS_INDIVIDUAL_STATE);
    });

    this.socket.on(SocketMessage.PERSON_CREATED, data => {
      const roomId = this.getRoomFullId('PERSON', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.PERSON_CREATED);
    });

    this.socket.on(SocketMessage.PHOTO_BOOK_EXPORT, data => {
      const roomId = this.getRoomFullId('PHOTO_BOOK', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.PHOTO_BOOK_EXPORT);
    });

    this.socket.on(SocketMessage.PHOTO_BOOK_EXPORT, data => {
      const roomId = this.getRoomFullId('PHOTO_BOOK_ARCHIVE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.PHOTO_BOOK_EXPORT);
    });

    this.socket.on(SocketMessage.BOOK_STATUS_CHANGED, data => {
      const roomId = this.getRoomFullId('BOOK', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.BOOK_STATUS_CHANGED);
    });

    this.socket.on(SocketMessage.BOOK_PRINT_PROGRESS, data => {
      const roomId = this.getRoomFullId('BOOK', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.BOOK_PRINT_PROGRESS);
    });

    this.socket.on(SocketMessage.PHOTO_BOOK_COMMENTS_CHANGED, data => {
      const roomId = this.getRoomFullId('BOOK', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.PHOTO_BOOK_COMMENTS_CHANGED);
    });

    // Своё имя, а не UPLOAD_FILE_VERSION_DATA: то событие по имени уходит в комнату FILE, а версии
    // файлов книги слушает комната книги.
    this.socket.on(SocketMessage.BOOK_FILE_VERSION_READY, data => {
      const roomId = this.getRoomFullId('BOOK', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.BOOK_FILE_VERSION_READY);
    });

    this.socket.on(SocketMessage.DESIGN_PACKAGE_ARCHIVE_EXPORT, data => {
      const roomId = this.getRoomFullId('DESIGN_PACKAGE_ARCHIVE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DESIGN_PACKAGE_ARCHIVE_EXPORT);
    });

    this.socket.on(SocketMessage.PRINT_PRODUCT_VERIFICATION_EXPORT, data => {
      const roomId = this.getRoomFullId('PRINT_PRODUCT_VERIFICATION', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.PRINT_PRODUCT_VERIFICATION_EXPORT);
    });

    this.socket.on(SocketMessage.DESIGN_INDD_CONVERT, data => {
      const roomId = this.getRoomFullId('DESIGN_PACKAGE_INDD', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DESIGN_INDD_CONVERT);
    });

    this.socket.on(SocketMessage.DESIGN_PSD_PARSE, data => {
      const roomId = this.getRoomFullId('DESIGN_PACKAGE_SPREAD', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DESIGN_PSD_PARSE);
    });

    // Image archive events
    this.socket.on(SocketMessage.IMAGE_ARCHIVE_PROGRESS, data => {
      const roomId = this.getRoomFullId('ARCHIVE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.IMAGE_ARCHIVE_PROGRESS);
    });

    this.socket.on(SocketMessage.IMAGE_ARCHIVE_COMPLETED, data => {
      const roomId = this.getRoomFullId('ARCHIVE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.IMAGE_ARCHIVE_COMPLETED);
    });

    this.socket.on(SocketMessage.IMAGE_ARCHIVE_ERROR, data => {
      const roomId = this.getRoomFullId('ARCHIVE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.IMAGE_ARCHIVE_ERROR);
    });

    // Demo project attach events
    this.socket.on(SocketMessage.DEMO_PROJECT_ATTACH_COMPLETED, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DEMO_PROJECT_ATTACH_COMPLETED);
    });

    this.socket.on(SocketMessage.DEMO_PROJECT_ATTACH_FAILED, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DEMO_PROJECT_ATTACH_FAILED);
    });

    this.socket.on(SocketMessage.DEMO_PHOTOBOOKS_READY, data => {
      const roomId = this.getRoomFullId('FILE', data.roomId);
      this.dispatchEventToRoom(roomId, data, SocketMessage.DEMO_PHOTOBOOKS_READY);
    });
  }

  private dispatchEventToRoom(roomId: string, data: any, eventType: SocketMessage) {
    const subject = this.subjects.find(s => s.roomId === roomId);
    if (subject) {
      subject.events$.next({ data, eventType });
    } else {
      console.warn(`No active subject found for room ${roomId}`);
    }
  }

  connect() {
    if (!this.socket.connected) {
      this.socket.connect();
    }
  }

  disconnect() {
    this.socket.disconnect();
  }

  joinRoom(type: RoomType, entityId: string): Observable<any> {
    const roomId = this.getRoomFullId(type, entityId);

    const existingSubject = this.subjects.find(s => s.roomId === roomId);
    if (existingSubject) {
      return existingSubject.events$.asObservable();
    }

    const newSubject: ChannelSubject = {
      roomId,
      events$: new Subject(),
    };

    this.subjects.push(newSubject);
    // Not connected yet: the connect handler joins every listed room, a join here would be the second one.
    if (this.socket.connected) this.emitJoin(roomId);

    return newSubject.events$.asObservable();
  }

  leaveRoom(type: RoomType, entityId: string) {
    const roomId = this.getRoomFullId(type, entityId);
    const index = this.subjects.findIndex(s => s.roomId === roomId);

    if (index === -1) return;

    this.subjects[index].events$.complete();
    this.subjects.splice(index, 1);

    this.socket.emit(SocketMessageIncoming.leaveUserFromRoom, { roomId });
  }

  private rejoinRooms() {
    for (const subject of this.subjects) {
      this.emitJoin(subject.roomId);
    }
  }

  // The join carries a ticket the server minted for this room on an authenticated request. When the
  // mint fails the join still goes out ticketless: the server is the one that decides, and dropping
  // the join here would silently kill a subscription the server would have accepted.
  private emitJoin(roomId: string) {
    this.roomTickets.requestTicket(roomId).subscribe(ticket => {
      const stillJoined = this.subjects.some(subject => subject.roomId === roomId);
      if (!stillJoined) return;

      this.socket.emit(SocketMessageIncoming.joinUserToRoom, { roomId, ticket });
    });
  }

  private getRoomFullId(type: RoomType, entityId: string): string {
    // Twin of the backend prefixes (socket/constants/constants.ts + WebSocketOperations) — a room
    // whose prefix drifts from the server's is refused by name, so the two move together. Комната
    // книги уже не близнец: имя приходит из общей модели, которую читает и бэкенд.
    const prefixMap: Record<RoomType, string> = {
      FILE: 'file:operations:',
      PERSON: 'person:operations:',
      BOOK: BOOK_SOCKET_ROOM_PREFIX,
      PHOTO_BOOK: 'photo:book:operations:',
      PHOTO_BOOK_ARCHIVE: 'photo:book:archive:',
      ARCHIVE: 'archive:operations:',
      FILE_FACES: 'file:faces:operations:',
      DESIGN_PACKAGE_ARCHIVE: 'design:package:archive:',
      DESIGN_PACKAGE_INDD: 'design:package:indd:',
      DESIGN_PACKAGE_SPREAD: 'design:package:spread:',
      PRINT_PRODUCT_VERIFICATION: 'print:product:verification:',
    };
    const prefix = prefixMap[type] || `${type}:`;
    return prefix + entityId;
  }
}
